import { FetchierOptions, fetchWithTimeout } from '../../services/fetchier';

interface RequestOptions extends FetchierOptions {
    method?: string;
    headers?: Record<string, string>;
    params?: Record<string, string>;
    body?: BodyInit | null;
}

interface APIResponse<T = unknown> {
    data: T;
    status: number;
    headers: Headers;
}

export class APIService {
    private baseUrl: string;
    private defaultHeaders: Record<string, string>;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        this.defaultHeaders = {};
    }

    private apiServerQueryParams(params: Record<string, string> = {}): string {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                searchParams.append(key, value.toString());
            }
        });
        return searchParams.toString();
    }

    private async request<T>(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<APIResponse<T>> {
        const {
            method = 'GET',
            headers = {},
            params = {},
            ...fetchOptions
        } = options;

        const normalizedEndpoint = endpoint.startsWith('/')
            ? endpoint
            : `/${endpoint}`;
        const queryString = this.apiServerQueryParams(params);
        const url = `${this.baseUrl}${normalizedEndpoint}${
            queryString ? `?${queryString}` : ''
        }`;

        const requestHeaders = { ...this.defaultHeaders, ...headers };
        const requestOptions: FetchierOptions = {
            ...fetchOptions,
            method,
            headers: requestHeaders,
        };

        const response = await fetchWithTimeout(url, requestOptions);
        const data = await response.json();

        return {
            data,
            status: response.status,
            headers: response.headers,
        };
    }

    public async get<T>(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<APIResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'GET' });
    }

    public async post<T>(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<APIResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'POST' });
    }

    public async put<T>(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<APIResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'PUT' });
    }

    public async delete<T>(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<APIResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'DELETE' });
    }
}
